import { vec3 } from 'gl-matrix';
import { Overlay, type OverlayQuad } from 'noodle/engine/overlay';

export interface BarOverlayOptions {
  /** Bar size in display pixels: [width, height]. Default [80, 8]. */
  size?: [number, number];
  /** Vertical pixel offset above the anchor (+Y is down, so use a negative value to float above). Default -40. */
  screenOffsetY?: number;
  /** Horizontal pixel offset (rarely used). Default 0. */
  screenOffsetX?: number;
  /** Fill amount in 0..1. Default 1. */
  fill?: number;
  /** Background color (drawn full-width). Default semi-transparent black. */
  bgColor?: [number, number, number, number];
  /** Fill color (drawn from left, width scales with `fill`). Default opaque red. */
  fillColor?: [number, number, number, number];
  /** When true, occludes behind world geometry. Default false. */
  depthTested?: boolean;
}

const scratch = vec3.create();

/**
 * Two-quad health bar: a background plate and a left-anchored fill bar. Both
 * quads share the anchor world position so they screen-project together.
 *
 * Game-UI building block — lives in playful rather than noodle since the
 * engine ships only generic overlay primitives (Overlay, TextOverlay,
 * CompositeOverlay).
 */
export class BarOverlay extends Overlay {
  public size: [number, number];
  public screenOffsetX: number;
  public screenOffsetY: number;
  public fill: number;
  public bgColor: [number, number, number, number];
  public fillColor: [number, number, number, number];
  public depthTested: boolean;

  constructor(opts: BarOverlayOptions = {}) {
    super();
    this.size = opts.size ?? [80, 8];
    this.screenOffsetX = opts.screenOffsetX ?? 0;
    this.screenOffsetY = opts.screenOffsetY ?? -40;
    this.fill = opts.fill ?? 1;
    this.bgColor = opts.bgColor ?? [0, 0, 0, 0.55];
    this.fillColor = opts.fillColor ?? [0.85, 0.18, 0.18, 1];
    this.depthTested = opts.depthTested ?? false;
  }

  public emit(out: OverlayQuad[]): void {
    const pos = this.resolvePosition(scratch);
    const w = this.size[0];
    const h = this.size[1];

    out.push({
      worldPos: [pos[0], pos[1], pos[2]],
      sizePx: [w, h],
      screenOffsetPx: [this.screenOffsetX, this.screenOffsetY],
      color: this.bgColor,
      depthTested: this.depthTested,
    });

    const clamped = Math.max(0, Math.min(1, this.fill));
    if (clamped <= 0) return;

    const fillWidth = clamped * w;
    // Fill is anchored to the left of the bar: shift its center right by
    // half the empty space.
    const fillCenterX = this.screenOffsetX - (w - fillWidth) * 0.5;

    out.push({
      worldPos: [pos[0], pos[1], pos[2]],
      sizePx: [fillWidth, h],
      screenOffsetPx: [fillCenterX, this.screenOffsetY],
      color: this.fillColor,
      depthTested: this.depthTested,
    });
  }
}
